import { ChevronDown } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

interface ScrollCueProps {
    /** The word above the chevron. */
    label?: string;
    className?: string;
}

/**
 * The "Scroll" cue at the foot of a full-screen hero.
 *
 * One definition, so the professional profile and the premium article carry the
 * same thing rather than two that nearly match — the article's had its own type
 * size, its own white and a `bounce` where the profile nudges, and the drift
 * was only visible side by side.
 *
 * The chevron's animation is `motion-safe`, so reduced motion gets the cue
 * without the movement.
 */
export const ScrollCue = ({ label = 'Scroll', className }: ScrollCueProps) => (
    // A word reading "Scroll" under a chevron invites a click, so it is a
    // control. `pointer-events-auto` because the hero's cue wrapper turns
    // pointer events off for the banner behind it.
    <button
        type="button"
        onClick={(event) => {
            const behavior = window.matchMedia(
                '(prefers-reduced-motion: reduce)',
            ).matches
                ? 'auto'
                : 'smooth';
            // The banner can be taller than the window, so what the reader
            // wants is the section after it rather than the end of the
            // picture. One screen down is the fallback when the cue is not
            // sitting in a section of its own.
            const next = event.currentTarget.closest('section')
                ?.nextElementSibling as HTMLElement | null;
            if (next) {
                next.scrollIntoView({ behavior });
            } else {
                window.scrollBy({ top: window.innerHeight, behavior });
            }
        }}
        className={cn(
            'pointer-events-auto flex cursor-pointer flex-col items-center gap-[0.5rem] border-0 bg-transparent p-0 font-sans text-[0.875rem] leading-none font-medium tracking-[0.02em] text-white/80',
            className,
        )}
    >
        {label}
        <ChevronDown
            aria-hidden="true"
            className="size-[1.125rem] motion-safe:animate-[scroll-cue-nudge_2s_ease-in-out_infinite]"
        />
    </button>
);
